import { useCallback, useMemo } from "react";
import { useUrlSearch } from "@/shared/hooks/useUrlSearch.ts";
import { useStagesToVerifyQuery } from "../api/queries";
import { useUpdateStatusMutation } from "../api/mutations";
import { verifySearchValues } from "../lib/options.ts";
import type {
  PaginationState,
  VerificationStage,
  VerifyQueryParams,
  VerifySearchValues,
} from "../types.ts";
import type { ColumnDef } from "@tanstack/react-table";
import {
  useBulkActions,
  type UseBulkActionsReturn,
} from "./use-bulk-actions.ts";
import { useReviewFlow, type UseReviewFlowReturn } from "./use-review-flow.ts";
import {
  useVerifySelection,
  type UseVerifySelectionReturn,
} from "./use-verify-selection.ts";
import { useVerifyColumns } from "./use-verify-columns.tsx";

interface UseVerifyPageReturn
  extends UseVerifySelectionReturn, UseBulkActionsReturn, UseReviewFlowReturn {
  data: VerificationStage[];
  columns: ColumnDef<VerificationStage>[];
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  refetch: () => void;
  // Search
  search: { show: boolean; values: VerifySearchValues };
  updateSearch: (params: {
    show?: boolean;
    values?: VerifySearchValues;
  }) => void;
  handleSearchSubmit: () => void;
  handleSearchReset: () => void;
  // Pagination
  pg: PaginationState;
  updatePg: (newPg: Partial<PaginationState>) => void;
}

export const useVerifyPage = (): UseVerifyPageReturn => {
  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  } = useUrlSearch<VerifySearchValues>({
    defaultValues: verifySearchValues,
  });

  const queryParams: VerifyQueryParams = useMemo(
    () => ({ page, perSize: size, search: appliedValues }),
    [page, size, appliedValues],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useStagesToVerifyQuery(queryParams);

  const data = queryData?.data || [];
  const totalItems = queryData?.totalItems || 0;

  // Shared: status-update mutation drives both bulk Accept and per-row review accept/reject.
  const acceptOrdersMutation = useUpdateStatusMutation();

  const selection = useVerifySelection();

  const reviewFlow = useReviewFlow({
    data,
    draftValues,
    acceptOrdersMutation,
  });

  const bulkActions = useBulkActions({
    selectedIds: selection.selectedIds,
    selectingAll: selection.selectingAll,
    draftValues,
    acceptOrdersMutation,
    clearSelection: selection.clearSelection,
  });

  const columns = useVerifyColumns(reviewFlow.openReview);

  const updatePg = useCallback(
    (newPg: Partial<PaginationState>) => {
      if (newPg.page !== undefined) setPage(newPg.page);
      if (newPg.size !== undefined) setSize(newPg.size);
    },
    [setPage, setSize],
  );

  const updateSearch = useCallback(
    (updates: { show?: boolean; values?: VerifySearchValues }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        Object.entries(updates.values).forEach(([key, value]) => {
          updateDraft(key as keyof VerifySearchValues, value);
        });
      }
    },
    [setShowFilters, updateDraft],
  );

  return {
    data,
    columns,
    isLoading,
    error: error as Error | null,
    totalItems,
    refetch,
    // Search
    search: { show: showFilters, values: draftValues },
    updateSearch,
    handleSearchSubmit: applySearch,
    handleSearchReset: resetSearch,
    // Pagination
    pg: { page, size },
    updatePg,
    // Selection / Bulk actions / Review flow
    ...selection,
    ...bulkActions,
    ...reviewFlow,
  };
};
